import React, { useState } from 'react';
import { Container, Row, Col, Card, CardBody, Form, FormGroup, Label, Input, Button, Alert } from 'reactstrap';
import axios from 'axios';
import { getNodeApiUrl } from '../config/api.config';

const PublicRegistration = () => {
    const [formData, setFormData] = useState({
        business: false,
        name: '',
        surname: '',
        company: '',
        vatNumber: '',
        email: '',
        mobile: '',
        password: '',
        confirmPassword: '',
        privacyAccepted: false
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [emailAlreadyExists, setEmailAlreadyExists] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setError('');
        setEmailAlreadyExists(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setEmailAlreadyExists(false);

        // Validazione frontend
        if (formData.password !== formData.confirmPassword) {
            setError('Le password non coincidono');
            return;
        }

        if (formData.password.length < 6) {
            setError('La password deve essere di almeno 6 caratteri');
            return;
        }

        if (!formData.privacyAccepted) {
            setError('Devi accettare il trattamento dei dati personali');
            return;
        }

        if (formData.business && !formData.company) {
            setError('La ragione sociale è obbligatoria per le aziende');
            return;
        }

        if (!formData.business && (!formData.name || !formData.surname)) {
            setError('Nome e cognome sono obbligatori');
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(`${getNodeApiUrl()}/api/public/register`, {
                business: formData.business,
                name: formData.name,
                surname: formData.surname,
                company: formData.company,
                vatNumber: formData.vatNumber,
                email: formData.email,
                mobile: formData.mobile,
                password: formData.password,
                privacyAccepted: formData.privacyAccepted,
                source: 'web' // Indica che la registrazione avviene dal sito web
            });

            if (!response.data.error_state) {
                setSuccess(true);
            }
        } catch (err) {
            if (err.response?.status === 409) {
                setEmailAlreadyExists(true);
                setError('Email già registrata nel sistema, contatta amministrazione@securfox.net per ottenere i tuoi dati di accesso');
            } else {
                setError(err.response?.data?.error_description || 'Errore durante la registrazione');
            }
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <Container className="mt-5">
                <Row className="justify-content-center">
                    <Col md={6}>
                        <Card>
                            <CardBody className="text-center py-5">
                                <img 
                                    src="https://storage.googleapis.com/installamelo/65a14921ed1be038f6185b53/logo.png"
                                    alt="Securfox Logo"
                                    style={{ height: '60px', marginBottom: '20px' }}
                                />
                                <i className="fas fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                                <h2 className="mt-4">Registrazione completata!</h2>
                                <p className="text-muted mt-3">
                                    Ti abbiamo inviato un'email all'indirizzo <strong>{formData.email}</strong>.
                                </p>
                                <p className="text-muted">
                                    Clicca sul link nell'email per attivare il tuo account.
                                </p>
                                <p className="text-muted mt-4">
                                    <small>Non hai ricevuto l'email? Controlla anche nella cartella spam.</small>
                                </p>
                                <Button 
                                    color="secondary" 
                                    className="mt-4"
                                    onClick={() => window.close()}
                                    style={{ 
                                        padding: '12px 48px'
                                    }}
                                >
                                    Chiudi
                                </Button>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container className="mt-5 mb-5">
            <Row className="justify-content-center">
                <Col md={8} lg={6}>
                    <Card>
                        <CardBody className="p-4">
                            <div className="text-center mb-4">
                                <img 
                                    src="https://storage.googleapis.com/installamelo/65a14921ed1be038f6185b53/logo.png"
                                    alt="Securfox Logo"
                                    style={{ height: '80px', marginBottom: '20px' }}
                                />
                                <h2 style={{ color: '#383838' }}>Registrati</h2>
                                <p className="text-muted">Crea il tuo account per accedere alla piattaforma</p>
                            </div>

                            {error && (
                                <Alert 
                                    color={emailAlreadyExists ? "warning" : "danger"} 
                                    className="mb-4"
                                >
                                    <div className="d-flex align-items-start">
                                        <i className={`fas ${emailAlreadyExists ? 'fa-info-circle' : 'fa-exclamation-circle'} me-2 mt-1`}></i>
                                        <div className="flex-grow-1">
                                            {emailAlreadyExists ? (
                                                <div>
                                                    <strong>Email già registrata</strong>
                                                    <p className="mb-2 mt-1">
                                                        L'indirizzo email <strong>{formData.email}</strong> è già presente nel sistema.
                                                    </p>
                                                    <p className="mb-2">
                                                        Per ottenere i tuoi dati di accesso, contatta:
                                                    </p>
                                                    <div className="d-flex align-items-center">
                                                        <i className="fas fa-envelope me-2"></i>
                                                        <a 
                                                            href={`mailto:amministrazione@securfox.net?subject=Richiesta dati di accesso&body=Salve, ho bisogno di recuperare i miei dati di accesso per l'email: ${formData.email}`}
                                                            className="text-decoration-none fw-bold"
                                                            style={{ color: '#856404' }}
                                                        >
                                                            amministrazione@securfox.net
                                                        </a>
                                                    </div>
                                                </div>
                                            ) : (
                                                error
                                            )}
                                        </div>
                                    </div>
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                {/* Tipo Account */}
                                <FormGroup>
                                    <Label className="fw-bold">Tipo di Account</Label>
                                    <div>
                                        <FormGroup check inline>
                                            <Label check>
                                                <Input
                                                    type="radio"
                                                    name="business"
                                                    checked={!formData.business}
                                                    onChange={() => setFormData(prev => ({ ...prev, business: false }))}
                                                />
                                                Privato
                                            </Label>
                                        </FormGroup>
                                        <FormGroup check inline>
                                            <Label check>
                                                <Input
                                                    type="radio"
                                                    name="business"
                                                    checked={formData.business}
                                                    onChange={() => setFormData(prev => ({ ...prev, business: true }))}
                                                />
                                                Azienda
                                            </Label>
                                        </FormGroup>
                                    </div>
                                </FormGroup>

                                {/* Campi Privato */}
                                {!formData.business && (
                                    <>
                                        <Row>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label for="name">Nome *</Label>
                                                    <Input
                                                        type="text"
                                                        name="name"
                                                        id="name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label for="surname">Cognome *</Label>
                                                    <Input
                                                        type="text"
                                                        name="surname"
                                                        id="surname"
                                                        value={formData.surname}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </>
                                )}

                                {/* Campi Azienda */}
                                {formData.business && (
                                    <>
                                        <FormGroup>
                                            <Label for="company">Ragione Sociale *</Label>
                                            <Input
                                                type="text"
                                                name="company"
                                                id="company"
                                                value={formData.company}
                                                onChange={handleChange}
                                                required
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label for="vatNumber">Partita IVA</Label>
                                            <Input
                                                type="text"
                                                name="vatNumber"
                                                id="vatNumber"
                                                value={formData.vatNumber}
                                                onChange={handleChange}
                                                placeholder="IT12345678901"
                                            />
                                        </FormGroup>
                                    </>
                                )}

                                {/* Email e Telefono */}
                                <FormGroup>
                                    <Label for="email">Email *</Label>
                                    <Input
                                        type="email"
                                        name="email"
                                        id="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label for="mobile">Cellulare *</Label>
                                    <Input
                                        type="tel"
                                        name="mobile"
                                        id="mobile"
                                        value={formData.mobile}
                                        onChange={handleChange}
                                        placeholder="+39 123 456 7890"
                                        required
                                    />
                                </FormGroup>

                                {/* Password */}
                                <FormGroup>
                                    <Label for="password">Password *</Label>
                                    <div className="position-relative">
                                        <Input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            id="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required
                                        />
                                        <i 
                                            className={`fas fa-eye${showPassword ? '-slash' : ''} position-absolute`}
                                            style={{ 
                                                right: '10px', 
                                                top: '50%', 
                                                transform: 'translateY(-50%)', 
                                                cursor: 'pointer',
                                                color: '#6c757d'
                                            }}
                                            onClick={() => setShowPassword(!showPassword)}
                                        ></i>
                                    </div>
                                    <small className="text-muted">Minimo 6 caratteri</small>
                                </FormGroup>

                                <FormGroup>
                                    <Label for="confirmPassword">Conferma Password *</Label>
                                    <Input
                                        type={showPassword ? "text" : "password"}
                                        name="confirmPassword"
                                        id="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        required
                                    />
                                </FormGroup>

                                {/* Privacy */}
                                <FormGroup check className="mt-4">
                                    <Label check>
                                        <Input
                                            type="checkbox"
                                            name="privacyAccepted"
                                            checked={formData.privacyAccepted}
                                            onChange={handleChange}
                                            required
                                        />
                                        Accetto il trattamento dei dati personali secondo la{' '}
                                        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                                            Privacy Policy
                                        </a>{' '}
                                        e i{' '}
                                        <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">
                                            Termini di Servizio
                                        </a>
                                        {' '}*
                                    </Label>
                                </FormGroup>

                                {/* Submit Button */}
                                <Button
                                    type="submit"
                                    block
                                    className="mt-4"
                                    disabled={loading}
                                    style={{ 
                                        backgroundColor: '#383838',
                                        borderColor: '#383838',
                                        padding: '12px'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#383838'}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Registrazione in corso...
                                        </>
                                    ) : (
                                        'Registrati'
                                    )}
                                </Button>

                                <div className="text-center mt-4">
                                    <p className="text-muted mb-0">
                                        Hai già un account?{' '}
                                        <a href="/login" style={{ color: '#383838', fontWeight: 'bold' }}>
                                            Accedi
                                        </a>
                                    </p>
                                </div>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default PublicRegistration;
